import { Fragment, useEffect, useRef, useState } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { FaCalendarAlt, FaCloudDownloadAlt } from 'react-icons/fa';
import styled from 'styled-components';
import { CustomButton, CustomSelect, CustomControl } from 'components/CustomForm';
import { Card, VDivider } from 'components/CommonWidgets';
import { useAdminSelector, useAdminDispatch } from 'admin-app/redux/admin.store';
import {
	pickToDate,
	pickFromDate,
	handleDateRadioChange,
	goToPage,
	buildFiltersQuery,
	handleInputChange,
	handleProvinceChange,
	handleUnionFilterChange,
	handleCompanyFilterChange,
	handleFilterTypeChange
} from 'admin-app/redux/reporting-filter.slice';
import {
	DefaultInputFilter,
	DefaultFilterType,
	SelfFilterType,
	DefaultCompanyFilter,
	DefaultUnionFilter,
	DateRadio
} from 'redux/global.consts';
import PersianPickerTile from 'components/PersianPickerTile';
import { useQuery } from 'react-query';
import { getIngredients, getProvinces, getUnions } from 'network/apis/InformationApi';
import { getCorporations } from 'network/apis/CorporationApi';
import { getInputs } from 'network/apis/CommonApi';
import useScreen from 'hooks/useScreen';

const FilterTypeOptions = [
	DefaultFilterType,
	SelfFilterType,
	{ value: 0, label: 'شرکت های تعاونی' },
	{ value: 1, label: 'اتحادیه های استانی' }
];

const Holder = styled(Card)`
	display: flex;
	flex-direction: ${(props) => props.ismobile ? 'column-reverse' : 'row'};
	align-items: center;
	justify-content: space-between;
`;

const Check = styled(Form.Check)`
    display: inline;
`;

const ResultBox = styled.div`
	display: grid;
	grid-template-rows: 1fr 1fr;
	row-gap: 0.5rem;

	& > div {
		display: grid;
		grid-template-columns: 1fr 1fr;
		text-align: center;
	}
`;

const FiltersBox = styled.div`
	width: ${(props) => props.ismobile ? '100%' : '75%'};
	padding: ${(props) => props.ismobile ? '0 2rem 0 2rem' : '0 1.5rem 0 2rem'};

	& > div.first-row {		
		width: 100%;
		display: flex;
		flex-direction: ${(props) => props.ismobile ? 'column' : 'row'};
		justify-content: space-between;
		align-items: center;

		div.pickers {
			display: flex;
			align-items: center;
			gap: 1rem;
		}

		div.checks {			
			display: flex;
			align-items: center;
			justify-content: center;
			width: ${(props) => props.ismobile ? '100%' : 'unset'};
			margin-top: ${(props) => props.ismobile ? '1rem' : 'unset'};

			&.arbitary {
				margin-left: ${(props) => props.ismobile ? '4rem' : 'unset'};
			}
		}
	}
	& > div.second-row {		
		width: 100%;
		margin-top: ${(props) => props.ismobile ? '1rem' : '1.5rem'};
		display: flex;
		flex-direction: ${(props) => props.ismobile ? 'column-reverse' : 'row'};
		justify-content: space-between;
		align-items: center;

		div.checks {
			display: flex;
			align-items: center;
			justify-content: center;
			width: ${(props) => props.ismobile ? '100%' : 'unset'};
		}
	}
`;

const Label = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const FormLabel = styled.label`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ReportHeader = ({
	currentTab,
	data,
	onDownloadTableExcel,
	onDownloadProvinceExcel,
}) => {
	const [refreshKey, setRefreshKey] = useState(0);

	const filterState = useAdminSelector((store) => store.reportingFilter);
	const dispatch = useAdminDispatch();

	const { lessThan1200 } = useScreen();

	useEffect(() => {
		setRefreshKey((prev) => prev + 1);
	}, [currentTab]);

	const getTabName = () => {
		if (currentTab === 'buy-report') return 'خرید';
		if (currentTab === 'distribute-report') return 'توزیع';
		if (currentTab === 'product-report') return 'تولید';

		return '-';
	};

	const { data: inputsData } = useQuery(
		["get-inputs"],
		() => getInputs(),
		{
			refetchOnWindowFocus: false,
		}
	);
	const getInputOtions = () => {
		if (inputsData?.data) {
			const inputOptions = inputsData.data.map((input, index) => {
				return { value: input?.item_id, label: input?.name };
			});

			return [DefaultInputFilter, ...inputOptions];
		}

		return [];
	};

	const { data: corporationsData } = useQuery(
		["get-corporations"],
		() => getCorporations(),
		{
			refetchOnWindowFocus: false,
		}
	);
	const getCorporationsOptions = () => {
		if (corporationsData?.data) {
			const corporationsOptions = corporationsData.data.map((corporation, index) => {
				return { value: corporation?.coop_id, label: corporation?.name };
			});

			return [DefaultCompanyFilter, ...corporationsOptions];
		}

		return [];
	};

	const { data: unionsData } = useQuery(
		["get-unions"],
		() => getUnions(),
		{
			refetchOnWindowFocus: false,
		}
	);
	const getUnionsOptions = () => {
		if (unionsData?.data) {
			const unionsOptions = unionsData.data.map((union, index) => {
				return { value: union?.union_id, label: union?.name };
			});

			return [DefaultUnionFilter, ...unionsOptions];
		}

		return [];
	};

	const getCalendarPickerTopOffset = () => {
		if (lessThan1200) {
			return '11rem';
		} else {
			return '11rem';
		}
	};

	const getCalendarPickerLeftOffset = (picker) => {
		if (picker === 'from') {
			if (lessThan1200) {
				return '19rem';
			} else {
				return '30rem';
			}
		}

		if (picker === 'to') {
			if (lessThan1200) {
				return '11rem';
			} else {
				return '22rem';
			}
		}
	};

	const extraFilters = () => {
		switch (filterState.selectedFilterType.value) {
			case 0:
				return (
					<CustomSelect
						placeholder='همه شرکت ها'
						className={`${lessThan1200 ? 'mb-3 w-50' : 'mx-2 w-25'}`}
						radius="14"
						options={getCorporationsOptions()}
						onChange={(newCorporation) => dispatch(handleCompanyFilterChange(newCorporation))}
						value={filterState.selectedCompany}
						menuPosition='fixed'
					/>
				);
			case 1:
				return (
					<CustomSelect
						placeholder='همه اتحادیه ها'
						className={`${lessThan1200 ? 'mb-3 w-50' : 'mx-2 w-25'}`}
						radius="14"
						options={getUnionsOptions()}
						onChange={(newUnion) => dispatch(handleUnionFilterChange(newUnion))}
						value={filterState.selectedUnion}
						menuPosition='fixed'
					/>
				);
			default:
				return null;

		}
	};

	const dispatchFilters = () => {
		dispatch(goToPage(1));
		dispatch(buildFiltersQuery());
	};

	return (
		<Holder ismobile={lessThan1200}>
			<div className='ms-4 me-5 d-flex flex-column align-items-center gap-2'>
				<CustomButton
					fontSize={10}
					onClick={onDownloadTableExcel}
					color='#0278EE'
					className='w-100'
				>
					اکسل جدول
				</CustomButton>
				<CustomButton
					fontSize={10}
					onClick={onDownloadProvinceExcel}
					color='#0278EE'
					className='w-100'
				>
					اکسل استانی
				</CustomButton>
			</div>
			<VDivider h={lessThan1200 ? '1.5rem' : '5rem'} className={`${lessThan1200 ? 'my-3' : ''}`} />
			<ResultBox className={`${lessThan1200 ? 'w-100' : 'w-25'}`}>
				<div>
					<Label>{`${data?.input_count ?? 0}`.persianCurrency()}</Label>
					<Label>:انواع نهاده ها</Label>
				</div>
				<div>
					<Label>{`${data?.sum ?? 0}`.persianCurrency()}</Label>
					<Label>:مجموع({data?.unit?.name})</Label>
				</div>
				<div>
					<Label>{`${data?.province_count ?? 0}`.persianCurrency()}</Label>
					<Label>:تعداد استانها</Label>
				</div>
			</ResultBox>
			<VDivider h={lessThan1200 ? '1.5rem' : '5rem'} className={`${lessThan1200 ? 'my-3' : ''}`} />
			<FiltersBox key={refreshKey} ismobile={lessThan1200}>
				<div className="first-row">
					<div className='pickers'>
						<PersianPickerTile
							currentDate={filterState.pickedDateTo}
							leftOffset={getCalendarPickerLeftOffset('to')}
							topOffset={getCalendarPickerTopOffset()}
							onPick={(pickedDate) => dispatch(pickToDate(pickedDate))}
						/>
						<span className="mx-2">تا</span>
						<PersianPickerTile
							currentDate={filterState.pickedDateFrom}
							leftOffset={getCalendarPickerLeftOffset('from')}
							topOffset={getCalendarPickerTopOffset()}
							onPick={(pickedDate) => dispatch(pickFromDate(pickedDate))}
						/>
					</div>
					<div className='checks arbitary'>
						<FormLabel htmlFor={DateRadio.CustomDate}>بازه زمانی دلخواه</FormLabel>
						<Check
							className="me-3 ms-2"
							type="radio"
							id={DateRadio.CustomDate}
							checked={filterState.selectedRadio === DateRadio.CustomDate}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
					<div className='checks'>
						<FormLabel htmlFor={DateRadio.LastMonth}>{getTabName(currentTab)} های یک ماه گذشته</FormLabel>
						<Check
							className="ms-2"
							type="radio"
							id={DateRadio.LastMonth}
							checked={filterState.selectedRadio === DateRadio.LastMonth}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
				</div>
				<div className="second-row">
					<CustomButton onClick={dispatchFilters}>
						نمایش
					</CustomButton>
					{extraFilters()}
					<CustomSelect
						placeholder='همه استان ها'
						className={`${lessThan1200 ? 'mb-3 w-50' : 'mx-2 w-25'}`}
						radius="14"
						options={[...FilterTypeOptions]}
						onChange={(newFilter) => dispatch(handleFilterTypeChange(newFilter))}
						value={filterState.selectedFilterType}
						menuPosition='fixed'
					/>
					<CustomSelect
						placeholder={currentTab === 'product-report' ? 'همه ترکیب ها' : 'همه نهاده ها'}
						className={`${lessThan1200 ? 'my-3 w-50' : 'mx-2 w-25'}`}
						radius="14"
						options={getInputOtions()}
						onChange={(newInput) => dispatch(handleInputChange(newInput))}
						value={filterState.selectedInput}
						menuPosition='fixed'
					/>
					<div className='checks'>
						<FormLabel htmlFor={DateRadio.LastThreeMonths}>
							{getTabName(currentTab)} های سه ماه گذشته
						</FormLabel>
						<Check
							className="ms-2"
							type="radio"
							id={DateRadio.LastThreeMonths}
							checked={filterState.selectedRadio === DateRadio.LastThreeMonths}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
				</div>
			</FiltersBox>
		</Holder>
	);
};

export default ReportHeader;
